import * as v0 from '../survey';

export type PaymentType =
  | 'ach'
  | 'givecard_rtp'
  | 'givecard_mail'
  | 'givecard_virtual'
  | 'usio_pickup'
  | 'usio_mail'
  | 'usio_virtual'
  | 'usbank_card_mail'
  | 'usbank_zelle';

export const IMPLEMENTED_PAYMENT_TYPES: PaymentType[] = [];

export const PAYMENT_PREFERENCES_TARGET_FIELD = 'applicant_payment_type';

export const DRAFT_SUFFIX = '_draft';

export const toDraftTargetField = (targetField: string) => targetField + DRAFT_SUFFIX;

export const PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD = toDraftTargetField(PAYMENT_PREFERENCES_TARGET_FIELD);

export const PAYMENT_PREFERENCES_EDITING_FIELD = 'payment_preferences_editing';

type PaymentField = {
  block: v0.Block,
  optional?: boolean,
};

type PaymentTypeFields = {
  fields: PaymentField[],
  slugs: string[],
};

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  ach: 'Direct deposit (ACH)',
  givecard_rtp: 'Instant bank deposit',
  givecard_mail: 'Debit card, mailed',
  givecard_virtual: 'Debit card, virtual',
  usio_pickup: 'Debit card, pickup',
  usio_mail: 'Debit card, mailed',
  usio_virtual: 'Debit card, virtual',
  usbank_card_mail: 'Debit card, mailed',
  usbank_zelle: 'Zelle®',
};

const BASE_SLUGS = ['payment_update_submitted', PAYMENT_PREFERENCES_EDITING_FIELD];

const BANK_ROUTING_NUMBER = { kind: 'BankRoutingNumber', targetField: 'bank_routing_number' } as v0.Block;
const BANK_ACCOUNT_NUMBER = { kind: 'Number', targetField: 'bank_account_number', format: 'bank_account_number' } as v0.Block;
const BANK_ACCOUNT_TYPE = {
  kind: 'Select',
  targetField: 'bank_account_type',
  choices: [
    { value: 'checking', label: { en: 'Checking' } },
    { value: 'savings', label: { en: 'Savings' } },
  ],
} as v0.Block;
const BANK_ACCOUNT_NAME = { kind: 'TextEntry', targetField: 'bank_account_name', format: 'legalName' } as v0.Block;
const BANK_ACCOUNT_NAME_OVERRIDE = { kind: 'Confirmation', targetField: 'bank_account_name_override' } as v0.Block;

const CARDHOLDER_SIGNATURE = { kind: 'InlineSignature', targetField: 'cardholder_signature' } as v0.Block;
const DISCLOSURE_SIGNATURE = { kind: 'InlineSignature', targetField: 'disclosure_signature' } as v0.Block;

const GIVECARD_MAILING_ADDRESS = { kind: 'Address', targetField: 'givecard_mailing_address' } as v0.Block;
const USIO_MAILING_ADDRESS = { kind: 'Address', targetField: 'usio_mailing_address' } as v0.Block;
const USBANK_MAILING_ADDRESS = { kind: 'Address', targetField: 'usbank_mailing_address' } as v0.Block;

const GIVECARD_CONTACT_SELECT = {
  kind: 'Select',
  targetField: 'gc_contact_select',
  choices: [
    { value: 'phone', label: { en: 'Phone Number' } },
    { value: 'email', label: { en: 'Email' } },
  ],
} as v0.Block;

const USIO_CARD_ID_SETUP_LINK = { kind: 'TextEntry', targetField: 'card_id_setup_link' } as v0.Block;

const ZELLE_CONTACT_SELECT = {
  kind: 'Select',
  targetField: 'zelle_contact_select',
  choices: [
    { value: 'existing_phone', label: { en: 'Phone number on file' } },
    { value: 'existing_email', label: { en: 'Email on file' } },
    { value: 'different_phone', label: { en: 'A different phone number' } },
    { value: 'different_email', label: { en: 'A different email' } },
    { value: 'no_account', label: { en: "I don't have a Zelle account" } },
  ],
} as v0.Block;
const ZELLE_NEW_PHONE = { kind: 'VerifiedContact', targetField: 'zelle_new_phone', mode: 'confirm', contactType: 'phone_number' } as v0.Block;
const ZELLE_NEW_EMAIL = { kind: 'VerifiedContact', targetField: 'zelle_new_email', mode: 'confirm', contactType: 'email' } as v0.Block;
const ZELLE_INTAKE = { kind: 'Zelle Intake', targetField: 'zelle_intake', contact_options: ['zelle_contact'] } as v0.Block;
const ZELLE_NAME_OVERRIDE = { kind: 'Confirmation', targetField: 'zelle_name_override' } as v0.Block;

const BANK_ACCOUNT_FIELDS: PaymentTypeFields = {
  fields: [
    { block: BANK_ROUTING_NUMBER },
    { block: BANK_ACCOUNT_NUMBER },
    { block: BANK_ACCOUNT_TYPE },
    { block: BANK_ACCOUNT_NAME },
    { block: BANK_ACCOUNT_NAME_OVERRIDE, optional: true },
  ],
  slugs: ['bank_account_name_score'],
};

const FIELDS_BY_PAYMENT_TYPE: Record<PaymentType, PaymentTypeFields> = {
  ach: BANK_ACCOUNT_FIELDS,
  givecard_rtp: BANK_ACCOUNT_FIELDS,
  givecard_mail: {
    fields: [{ block: CARDHOLDER_SIGNATURE }, { block: GIVECARD_MAILING_ADDRESS }],
    slugs: [],
  },
  givecard_virtual: {
    fields: [{ block: CARDHOLDER_SIGNATURE }, { block: GIVECARD_CONTACT_SELECT, optional: true }],
    slugs: ['preferred_gc_contact'],
  },
  usio_pickup: {
    fields: [],
    slugs: [],
  },
  usio_mail: {
    fields: [{ block: USIO_MAILING_ADDRESS }],
    slugs: [],
  },
  usio_virtual: {
    fields: [{ block: USIO_CARD_ID_SETUP_LINK, optional: true }],
    slugs: [],
  },
  usbank_card_mail: {
    fields: [{ block: DISCLOSURE_SIGNATURE }, { block: USBANK_MAILING_ADDRESS }],
    slugs: ['usbank_disclosure_mailcard'],
  },
  usbank_zelle: {
    fields: [
      { block: ZELLE_CONTACT_SELECT },
      { block: ZELLE_NEW_PHONE, optional: true },
      { block: ZELLE_NEW_EMAIL, optional: true },
      { block: ZELLE_INTAKE, optional: true },
      { block: ZELLE_NAME_OVERRIDE, optional: true },
    ],
    slugs: ['zelle_contact', 'zelle_contact_draft', 'zelle_legal_name', 'zelle_name_score'],
  },
};

export type PaymentPreferencesOption = {
  paymentType: PaymentType,
  excludeWhen?: v0.BooleanExpr,
};

export function getPaymentPreferencesOptions(component: v0.PaymentPreferences): PaymentPreferencesOption[] {
  const options: PaymentPreferencesOption[] = [];
  for (const vendor of component.vendors) {
    const vendorOptions: PaymentPreferencesOption[] =
      vendor.kind === 'Dwolla Payment Options' ? vendor.dwollaOptions
        : vendor.kind === 'GiveCard Payment Options' ? vendor.giveCardOptions
          : vendor.kind === 'USIO Payment Options' ? vendor.usioOptions
            : vendor.usBankOptions;
    options.push(...vendorOptions);
  }
  return options;
}

export function getPaymentPreferencesPaymentTypes(component: v0.PaymentPreferences): PaymentType[] {
  return [...new Set(getPaymentPreferencesOptions(component).map(option => option.paymentType))];
}

function toDraftBlock(block: v0.Block): v0.Block {
  const { targetField, contact_options } = block as { targetField: string, contact_options?: string[] };
  return {
    ...block,
    targetField: toDraftTargetField(targetField),
    ...(contact_options ? { contact_options: contact_options.map(toDraftTargetField) } : {}),
  } as v0.Block;
}

const getTargetField = (block: v0.Block) => (block as { targetField: string }).targetField;

export const PAYMENT_PREFERENCES_DRAFT_PAYMENT_TYPE_CONDITION: v0.BooleanExpr = {
  kind: 'Or',
  clauses: [
    { kind: 'DoesntExist', field: PAYMENT_PREFERENCES_TARGET_FIELD },
    { kind: 'Exists', field: PAYMENT_PREFERENCES_EDITING_FIELD },
  ],
};

/** The select where the applicant picks a payment type, offering each configured type once. */
export function getPaymentPreferencesDraftPaymentTypeBlock(component: v0.PaymentPreferences): v0.Block {
  const choices = new Map<PaymentType, { value: PaymentType, label: { en: string }, excludeWhen?: v0.BooleanExpr }>();
  for (const option of getPaymentPreferencesOptions(component)) {
    if (!choices.has(option.paymentType)) {
      choices.set(option.paymentType, {
        value: option.paymentType,
        label: { en: PAYMENT_TYPE_LABELS[option.paymentType] },
        ...(option.excludeWhen ? { excludeWhen: option.excludeWhen } : {}),
      });
    }
  }
  return {
    kind: 'Select',
    targetField: PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD,
    content: { en: 'Choose a payment method' },
    choices: [...choices.values()],
  } as v0.Block;
}

export type PaymentPreferencesDraftRow = {
  block: v0.Block,
  condition: v0.BooleanExpr,
  optional: boolean,
  paymentTypes: PaymentType[],
};

/**
 * One draft row per field of the implemented payment types. A field shared by several types
 * (e.g. a signature) becomes a single row shown when any of those types is selected.
 */
export function getPaymentPreferencesDraftRows(component: v0.PaymentPreferences): PaymentPreferencesDraftRow[] {
  const rows = new Map<string, { block: v0.Block, optional: boolean, paymentTypes: PaymentType[] }>();
  for (const paymentType of getPaymentPreferencesPaymentTypes(component)) {
    if (!IMPLEMENTED_PAYMENT_TYPES.includes(paymentType)) continue;
    for (const { block, optional } of FIELDS_BY_PAYMENT_TYPE[paymentType].fields) {
      const draftBlock = toDraftBlock(block);
      const row = rows.get(getTargetField(draftBlock));
      if (row) {
        row.paymentTypes.push(paymentType);
      } else {
        rows.set(getTargetField(draftBlock), { block: draftBlock, optional: !!optional, paymentTypes: [paymentType] });
      }
    }
  }
  return [...rows.values()].map(({ block, optional, paymentTypes }) => ({
    block,
    optional,
    paymentTypes,
    condition: {
      kind: 'Or',
      clauses: paymentTypes.map(paymentType => ({ kind: 'Equals', field: PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD, value: paymentType })),
    } as v0.BooleanExpr,
  }));
}

export function getPaymentPreferencesRealBlocks(component: v0.PaymentPreferences): v0.Block[] {
  const blocks = new Map<string, v0.Block>();
  for (const paymentType of getPaymentPreferencesPaymentTypes(component).filter(paymentType => IMPLEMENTED_PAYMENT_TYPES.includes(paymentType))) {
    for (const { block } of FIELDS_BY_PAYMENT_TYPE[paymentType].fields) {
      blocks.set(getTargetField(block), block);
    }
  }
  return [...blocks.values()];
}

/** Every draft key: the draft payment type plus the draft rows' target fields. */
export function getPaymentPreferencesDraftTargetFields(component: v0.PaymentPreferences): string[] {
  return [
    PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD,
    ...getPaymentPreferencesDraftRows(component).map(row => getTargetField(row.block)),
  ];
}

/** The draft keys that apply to one payment type: the draft payment type plus that type's rows. */
export function getPaymentPreferencesDraftTargetFieldsForType(component: v0.PaymentPreferences, paymentType: PaymentType): string[] {
  return [
    PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD,
    ...getPaymentPreferencesDraftRows(component)
      .filter(row => row.paymentTypes.includes(paymentType))
      .map(row => getTargetField(row.block)),
  ];
}

export function getPaymentPreferencesSlugs(component: v0.PaymentPreferences): string[] {
  return [
    ...BASE_SLUGS,
    ...getPaymentPreferencesPaymentTypes(component).filter(paymentType => IMPLEMENTED_PAYMENT_TYPES.includes(paymentType)).flatMap(paymentType => FIELDS_BY_PAYMENT_TYPE[paymentType].slugs),
  ];
}

export function getPaymentPreferencesTargetFields(component: v0.PaymentPreferences): string[] {
  return [...new Set([
    PAYMENT_PREFERENCES_TARGET_FIELD,
    PAYMENT_PREFERENCES_DRAFT_TARGET_FIELD,
    PAYMENT_PREFERENCES_EDITING_FIELD,
    ...getPaymentPreferencesRealBlocks(component).map(getTargetField),
    ...getPaymentPreferencesDraftTargetFields(component),
  ])];
}

/** Replaces the kind with ordinary blocks: the draft select, the draft rows, then the kind itself last. */
export function expandPaymentPreferences(component: v0.PaymentPreferences & { conditionalOn?: v0.BooleanExpr | v0.Code }): v0.Block[] {
  // The kind's own conditionalOn gates every expanded block, so an enclosing Conditional Block
  // can no longer override the per-row conditions.
  const parentCondition: v0.BooleanExpr | undefined = typeof component.conditionalOn === 'string'
    ? { kind: 'Code Boolean Expr', code: component.conditionalOn } as v0.BooleanExpr
    : component.conditionalOn;
  const gate = (condition: v0.BooleanExpr): v0.BooleanExpr =>
    parentCondition ? { kind: 'And', clauses: [parentCondition, condition] } : condition;

  const draftPaymentTypeBlock = {
    ...getPaymentPreferencesDraftPaymentTypeBlock(component),
    optional: true,
    conditionalOn: gate(PAYMENT_PREFERENCES_DRAFT_PAYMENT_TYPE_CONDITION),
  } as v0.Block;

  const draftBlocks = getPaymentPreferencesDraftRows(component).map(row => ({
    ...row.block,
    ...(row.optional ? { optional: true } : {}),
    conditionalOn: gate(row.condition),
  }) as v0.Block);

  return [draftPaymentTypeBlock, ...draftBlocks, { ...component, _expanded: true } as v0.Block];
}
